import React, { useState } from 'react';
import { Badge, Button, Card, Notice, Radio, Text } from '@heartinternet/hi-ui';
import type { SSLInfoContext } from '../SSLInfoPage';
import type { ControlChoice, DcvData, SSLCheckData, SSLInfoData } from '../types';
import { DCV_EMAIL_USERNAMES, endpoints, removeSubFromDomain, rpcGet, rpcPost } from '../helpers';
import { ShowMeHowModal, type GuideKind } from './ShowMeHowModal';

export interface DomainOwnershipProps {
  info: SSLInfoData;
  dcv: DcvData;
  ctx: SSLInfoContext;
}

interface Message {
  text: string;
  isError: boolean;
}

const RESEND_FRIENDLY_ERROR =
  "We couldn't resend the domain validation request. Please try again or contact support for assistance.";

/** Domain Ownership (DCV) card — mirrors submitted/pending-domain-ownership.js. */
export const DomainOwnership: React.FC<DomainOwnershipProps> = ({ info, dcv, ctx }) => {
  // All hooks must run before any early return, so the hook order stays stable if
  // dcv.status transitions (e.g. into FINALISING) without a remount — Rules of Hooks.
  const [method, setMethod] = useState<ControlChoice>(dcv.type ?? 'DNS');
  const [emailUser, setEmailUser] = useState<string>('admin');
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [guide, setGuide] = useState<GuideKind | null>(null);

  if (dcv.status === 'FINALISING') {
    return (
      <Card
        variant="surface"
        header={
          <div className="hi-ui-ssl-info__section-header">
            <Text variant="heading-3">Domain ownership</Text>
            <Badge variant="info">Finalising</Badge>
          </div>
        }
      >
        <div className="hi-ui-ssl-info__section-body">
          <Text variant="body">
            The ownership process of your certificate is nearing completion. Check back shortly or
            wait for an email from us to confirm when your certificate is ready.
          </Text>
        </div>
      </Card>
    );
  }

  /* v8 ignore next -- common_name is always set on a pending cert; '' guards the nullable type */
  const commonName = info.common_name ?? '';
  const registrableDomain = removeSubFromDomain(commonName);
  const displayDomain = dcv.email ? dcv.email.split('@')[1] : registrableDomain;

  const handleCheck = async () => {
    setMessage(null);
    setChecking(true);

    const dcvEntry: { commonName: string; type: ControlChoice; email?: string } = {
      commonName,
      type: method,
    };
    if (method === 'EMAIL') dcvEntry.email = `${emailUser}@${registrableDomain}`;

    const resend = await rpcPost(ctx.fetchFn, endpoints.resend(ctx.baseUrl, ctx.oid), {
      dcv: [dcvEntry],
    });
    if (resend.error) {
      setChecking(false);
      const text =
        resend.error === RESEND_FRIENDLY_ERROR
          ? 'Failed to update verification method, please try again or contact support.'
          : resend.error;
      setMessage({ text, isError: true });
      return;
    }

    const check = await rpcGet<SSLCheckData>(ctx.fetchFn, endpoints.check(ctx.baseUrl, ctx.oid));
    setChecking(false);
    if (check.error || !check.data) {
      setMessage({ text: check.error ?? 'We could not check the status.', isError: true });
      return;
    }
    if (check.data.status === 'PENDING') {
      setMessage({ text: 'Status is still pending. Please try again later.', isError: false });
      return;
    }
    ctx.refetch();
  };

  const optionClass = (choice: ControlChoice) =>
    `hi-ui-ssl-info__dcv-option${method === choice ? '' : ' hi-ui-ssl-info__dcv-option--disabled'}`;

  return (
    <Card
      variant="surface"
      header={
        <div className="hi-ui-ssl-info__section-header">
          <Text variant="heading-3">Domain ownership</Text>
          <Badge variant="warning">Pending</Badge>
        </div>
      }
    >
      <div className="hi-ui-ssl-info__section-body">
        <Text variant="body">
          Please choose from the following options to prove domain control. You only need to
          complete one of the following options.
        </Text>

        {/* DNS */}
        <div className={optionClass('DNS')}>
          <Radio
            name="controlChoice"
            value="DNS"
            checked={method === 'DNS'}
            onChange={() => setMethod('DNS')}
            label="DNS (easiest option)"
          />
          <div className="hi-ui-ssl-info__dcv-body">
            <Text variant="body-small">Add a CNAME record.</Text>
            <div className="hi-ui-ssl-info__dcv-action">
              <Button variant="tertiary" onClick={() => setGuide('dns')}>
                Show me how
              </Button>
            </div>
          </div>
        </div>

        {/* Email */}
        <div className={optionClass('EMAIL')}>
          <Radio
            name="controlChoice"
            value="EMAIL"
            checked={method === 'EMAIL'}
            onChange={() => setMethod('EMAIL')}
            label="Email"
          />
          <div className="hi-ui-ssl-info__dcv-body">
            <Text variant="body-small">
              Send a verification link to the following email address.
            </Text>
            <ul className="hi-ui-ssl-info__email-list">
              {DCV_EMAIL_USERNAMES.map((user) => (
                <li key={user}>
                  <Radio
                    name="verifyEmail"
                    value={`${user}@${displayDomain}`}
                    checked={emailUser === user}
                    disabled={method !== 'EMAIL'}
                    onChange={() => setEmailUser(user)}
                    label={`${user}@${displayDomain}`}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Website (FILE) — hidden for wildcard certs */}
        {!info.is_wildcard && (
          <div className={optionClass('FILE')}>
            <Radio
              name="controlChoice"
              value="FILE"
              checked={method === 'FILE'}
              onChange={() => setMethod('FILE')}
              label="Website"
            />
            <div className="hi-ui-ssl-info__dcv-body">
              <Text variant="body-small">Store a web page in your website.</Text>
              <div className="hi-ui-ssl-info__dcv-action">
                <Button variant="tertiary" onClick={() => setGuide('website')}>
                  Show me how
                </Button>
              </div>
            </div>
          </div>
        )}

        <Text variant="body-small">
          Check status with the button or by refreshing the page. Validation by DNS and Website
          normally completes in 1-2 hours so please come back periodically.
        </Text>

        {message && <Notice variant={message.isError ? 'error' : 'success'}>{message.text}</Notice>}

        <div className="hi-ui-ssl-info__actions">
          <Button variant="primary" loading={checking} onClick={handleCheck}>
            Check status
          </Button>
        </div>
      </div>

      {guide && (
        <ShowMeHowModal
          kind={guide}
          isOpen
          onClose={() => setGuide(null)}
          info={info}
          manageDomainUrl={ctx.manageDomainUrl}
        />
      )}
    </Card>
  );
};
